import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { SimpleVideoPlayerModal } from '@/components/SimpleVideoPlayerModal';
import PlayPreview from '@/images/PlayPreview.svg';
import TutorialBg from '@/images/tutorial-bg.png';
import { VideoBadge } from '@/pages/home/components/Common';
import { useAnalytics } from '@/services/analytics';
import styled from '@emotion/styled';
import { useState } from 'react';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 12px;
`;

const PlayWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    width: 36px;
    height: 36px;

    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(1);

    border-radius: 50%;
    border: 1px solid ${({ theme }) => theme.colors.black[200]};
    background: white;

    transition: all 0.3s ease-in-out;
`;

const ImageContainer = styled.div<{ bg: string }>`
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
    background-color: ${({ theme }) => theme.colors.black[100]};
    background-image: url(${props => props.bg});
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    cursor: pointer;
    position: relative;
    box-shadow:
        0.3px 0.8px 1.1px 0px rgba(187, 187, 187, 0.11),
        0.5px 1.3px 1.8px -0.6px rgba(187, 187, 187, 0.18),
        1.1px 2.7px 3.8px -1.2px rgba(187, 187, 187, 0.26);

    width: 284px;
    aspect-ratio: 16/10;
    transition: all 0.3s ease-in-out;

    :hover {
        background-color: ${({ theme }) => theme.colors.black[0]};

        ${PlayWrapper} {
            transform: translate(-50%, -50%) scale(1.25);
        }
    }
`;

const Title = styled.div`
    color: ${({ theme }) => theme.colors.secondary[900]};
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.32px;
    margin: 20px 24px 0;
`;

export interface Tutorial {
    id: string;
    url: string;
    title: string;
    thumbnail?: string;
    duration: number;
    button?: {
        label: string;
        onClick: () => Promise<void>;
    };
}

interface Props {
    tutorial: Tutorial;
}

export const TutorialTile = ({ tutorial }: Props) => {
    const mixpanel = useAnalytics();
    const [isOpen, setOpen] = useState(false);
    const handleClick = () => {
        mixpanel?.track('workspace-item', { event: 'click', section: 'tutorial', title: tutorial.title, id: tutorial.id });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    if (!tutorial.url) {
        return null;
    }

    return (
        <>
            <Container id={tutorial.id} onClick={handleClick}>
                <ImageContainer bg={tutorial.thumbnail || TutorialBg}>
                    <Title>{tutorial.title}</Title>

                    <PlayWrapper>
                        <MaskedIcon src={PlayPreview} size="20px" />
                    </PlayWrapper>

                    <VideoBadge duration={tutorial.duration} />
                </ImageContainer>
            </Container>

            <SimpleVideoPlayerModal
                isOpen={isOpen}
                handleClose={handleClose}
                title={tutorial.title}
                src={tutorial.url}
                thumbnail={tutorial.thumbnail || ''}
                button={tutorial.button}
                autoplay
            />
        </>
    );
};
